import type { ChatMessage } from "../types";

const DB_NAME = "nexus-local";
const DB_VERSION = 1;
const MESSAGE_STORE = "messages";

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.addEventListener("upgradeneeded", () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(MESSAGE_STORE)) {
        const store = database.createObjectStore(MESSAGE_STORE, { keyPath: "id" });
        store.createIndex("createdAt", "createdAt");
      }
    });
    request.addEventListener("success", () => resolve(request.result));
    request.addEventListener("error", () => reject(request.error ?? new Error("IndexedDBを開けませんでした。")));
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.addEventListener("complete", () => resolve());
    transaction.addEventListener("abort", () => reject(transaction.error ?? new Error("IndexedDB処理を中断しました。")));
    transaction.addEventListener("error", () => reject(transaction.error ?? new Error("IndexedDB処理に失敗しました。")));
  });
}

export async function loadMessagesFromDb(): Promise<ChatMessage[]> {
  const database = await openDatabase();
  try {
    const transaction = database.transaction(MESSAGE_STORE, "readonly");
    const request = transaction.objectStore(MESSAGE_STORE).index("createdAt").getAll();
    const messages = await new Promise<ChatMessage[]>((resolve, reject) => {
      request.addEventListener("success", () => resolve((request.result as ChatMessage[]).slice(-100)));
      request.addEventListener("error", () => reject(request.error ?? new Error("履歴を読み込めませんでした。")));
    });
    await transactionDone(transaction);
    return messages;
  } finally {
    database.close();
  }
}

export async function persistMessages(messages: ChatMessage[]): Promise<void> {
  const database = await openDatabase();
  try {
    const transaction = database.transaction(MESSAGE_STORE, "readwrite");
    const store = transaction.objectStore(MESSAGE_STORE);
    store.clear();
    for (const message of messages.slice(-100)) {
      store.put({ ...message, state: message.state === "streaming" ? "complete" : message.state });
    }
    await transactionDone(transaction);
  } finally {
    database.close();
  }
}

export async function clearMessagesFromDb(): Promise<void> {
  const database = await openDatabase();
  try {
    const transaction = database.transaction(MESSAGE_STORE, "readwrite");
    transaction.objectStore(MESSAGE_STORE).clear();
    await transactionDone(transaction);
  } finally {
    database.close();
  }
}

